import { BeneficiarySummaryMapper } from "@beneficiary/data/mappers/beneficiary-summary.mapper";
import { BeneficiarySummary } from "@beneficiary/domain/models/beneficiary-summary.model";
import { DerivationDto } from "@catalogue/data/dto/derivation.dto";
import { ServiceMapper } from "@catalogue/data/mappers/service/service.mapper";
import { Derivation } from "@catalogue/domain/models/derivation.model";
import { StatusEnum } from "@catalogue/domain/models/status.model";
import { DocumentMapper } from "@core/data/mappers/document.mapper";
import { IncDocument } from "@core/domain/models/inc-document.model";
import { Nullable } from "@core/domain/types/nullable.type";
import { Entity } from "@entity/domain/models/entity.model";
import { inject, injectable } from "inversify";

@injectable()
export class DerivationMapper {
    constructor(
        @inject(ServiceMapper)
        private readonly serviceMapper: ServiceMapper,
        @inject(BeneficiarySummaryMapper)
        private readonly beneficiaryMapper: BeneficiarySummaryMapper,
        @inject(DocumentMapper)
        private readonly documentMapper: DocumentMapper,
    ) {}

    map(dto: DerivationDto): Nullable<Derivation> {
        if (!dto) return null;

        let beneficiary = null;
        let service = null;

        // Check if beneficiary is an object or just an ID
        if (dto.beneficiary) {
            if (typeof dto.beneficiary === "object") {
                // Use type assertion to tell TypeScript what kind of object this is
                const beneficiaryObj = dto.beneficiary as any; // or as BeneficiarySummaryDto

                if (!beneficiaryObj.projects_data) {
                    beneficiaryObj.projects_data = [];
                }
                beneficiary = this.beneficiaryMapper.map(beneficiaryObj);
            } else {
                // It's just an ID, need to fetch the full beneficiary or create a minimal object
                beneficiary = new BeneficiarySummary(
                    dto.beneficiary,
                    `${dto.beneficiary_data?.name} ${dto.beneficiary_data?.surname}` ||
                        `Beneficiario ${dto.beneficiary}`,
                    "",
                    "",
                    [],
                );
            }
        }

        // Similar logic for service
        if (dto.service) {
            if (typeof dto.service === "object") {
                service = this.serviceMapper.map(dto.service);
            } else {
                // It's just an ID, create minimal Service object as plain object
                // Use service_data if available, otherwise use formatted ID string
                let categoryName =
                    dto.service_data?.category || `Servicio ${dto.service}`;

                service = {
                    id: dto.service,
                    category: categoryName,
                    entity: dto.assigned_by,
                    createdAt: new Date().toISOString(),
                };
            }
        }

        if (!beneficiary || !service) return null;

        const documentsData: IncDocument[] =
            dto.documents_data?.mapNotNull((doc) =>
                this.documentMapper.map(doc),
            ) ?? [];
        let authorizationFile: IncDocument | undefined = undefined;
        if (dto.authorization_file_data) {
            const mappedAuthFile = this.documentMapper.map(
                dto.authorization_file_data,
            );
            if (mappedAuthFile) {
                authorizationFile = mappedAuthFile;
            }
        }

        const entity = dto.entity_data
            ? new Entity(dto.entity_data.id, dto.entity_data.company_name, true)
            : undefined;

        try {
            return new Derivation(
                dto.id,
                beneficiary,
                service,
                dto.assigned_by,
                dto.entity,
                new Date(dto.created_at),
                undefined,
                entity,
                dto.status as StatusEnum,
                dto.reason,
                documentsData,
                authorizationFile,
                dto.notify_admin_id,
            );
        } catch (error) {
            console.error("Error mapping DerivationDto:", error, dto);
            return null;
        }
    }
}
